// src/pages/MyAccount.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom'; 
import { useAuth } from '../context/AuthContext'; 
import { updateProfile, getAuth } from 'firebase/auth'; 
import { doc, getDoc } from 'firebase/firestore';
import { db } from "../config/firebaseConfig";
import LoadingSpinner from '../components/LoadingSpinner';
import UserSubscription from '../components/UserSubscription';
import SubscriptionHistory from '../components/SubscriptionHistory';
import ConfirmationModal from '../components/ConfirmationModal';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
console.log(process.env.REACT_APP_BACKEND_URL)
console.log(BACKEND_URL)
const MyAccount = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [updateError, setUpdateError] = useState('');
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);

  useEffect(() => {
    if (!loading && !user) {
      // If not loading and user is not authenticated, redirect to login
      navigate('/login');
    } else if (user) {
      // If user is authenticated, populate the form with the user's info
      const names = user.displayName ? user.displayName.split(' ') : ['', ''];
      setFirstName(names[0] || '');
      setLastName(names.slice(1).join(' ') || '');
      setEmail(user.email || '');
    }
  }, [user, loading, navigate]);

  const handleUpdateProfile = async () => {
    setUpdateSuccess(false);
    setUpdateError('');
    
    const auth = getAuth();
    try {
      await updateProfile(auth.currentUser, {
        displayName: `${firstName} ${lastName}`.trim(),
      });
      setUpdateSuccess(true);
      setTimeout(() => setUpdateSuccess(false), 3000);
    } catch (error) {
      setUpdateError('Error updating profile: ' + error.message);
    }
  };

  const handleManageSubscription = async () => {
    try {
      if (!user) return;
      
      setUpdateError('');
      // Show loading state if you want
      
      // First, try to get the customerId from Firestore
      const userDocRef = doc(db, 'users', user.email);
      const userDoc = await getDoc(userDocRef);
      let customerId = null;
      
      if (userDoc.exists()) {
        customerId = userDoc.data().customerId;
      }
      
      // If not found in user document, check purchases collection
      if (!customerId) {
        const purchaseRef = doc(db, 'purchases', user.uid);
        const purchaseDoc = await getDoc(purchaseRef);
        
        if (purchaseDoc.exists()) {
          customerId = purchaseDoc.data().customerId;
        }
      }
      
      // If still not found, show error
      if (!customerId) {
        setUpdateError('Customer ID not found. Please contact support.');
        return;
      }
      
      // Call backend API to create Stripe customer portal session
      const response = await axios.post(
        `${BACKEND_URL}/api/create-portal-session`,
        { 
          email: user.email,
          customerId: customerId,
          returnUrl: window.location.href
        }
      );
      
      if (response.data.success && response.data.url) {
        // Open Stripe Customer Portal in a new tab
        window.open(response.data.url, '_blank');
      } else {
        throw new Error('Failed to create portal session');
      }
    } catch (error) {
      console.error('Error creating portal session:', error);
      setUpdateError('Error accessing subscription management portal. Please try again later.');
    }
  };

  const handleDeleteAccount = () => {
    setIsDeleteModalVisible(true);
  };

  const confirmDeleteAccount = async () => {
    try {
      setIsDeletingAccount(true);
      setDeleteError('');

      // TODO: Implement account deletion logic
      // This would typically involve:
      // 1. Canceling any active subscriptions
      // 2. Deleting user documents from Firestore
      // 3. Deleting the user's authentication account
      
      // For now, just simulate the process
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      navigate('/login');
    } catch (error) {
      console.error('Error deleting account:', error);
      setDeleteError('Error deleting account: ' + error.message);
    } finally {
      setIsDeletingAccount(false);
      setIsDeleteModalVisible(false);
    }
  };

  // Show a loading message while waiting for the authentication check
  if (loading) return <LoadingSpinner />;

  return (
    <>
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto mt-6 mb-12 text-center">
          <h2 className="text-2xl font-bold">Account Settings</h2>
          <p className="text-gray-600 mt-4 text-md">
            Update your profile, check your plan, or update your plan.
          </p>
        </div>
        
        {/* Account Section */}
        <div className="mb-8 p-6 bg-white rounded-lg border">
          <h2 className="text-xl font-semibold mb-4">Profile</h2>
          
          {updateSuccess && (
            <div className="mb-4 p-3 bg-green-100 text-green-700 rounded">
              Profile updated successfully!
            </div>
          )}
          
          {updateError && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
              {updateError}
            </div>
          )}
          
          <div className="grid grid-cols-2 gap-4 mb-4">
            <input
              type="text"
              placeholder="First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="appearance-none border rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            />
            <input
              type="text"
              placeholder="Last Name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="appearance-none border rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            />
          </div>
          <input
            type="email"
            placeholder="Email"
            value={email}
            disabled
            className="appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline mb-4"
          />
          <button
            onClick={handleUpdateProfile}
            className="btn-theme text-white py-2 px-4 rounded"
          >
            Update
          </button>
        </div>

        {/* Subscription Section */}
        <div className="mb-8 p-6 bg-white rounded-lg border">
          <h2 className="text-xl font-semibold mb-4">Current Plan</h2>
          
          <UserSubscription />
          
          {/* <button
  onClick={handleManageSubscription}
  className="bg-gray-500 hover:bg-gray-700 text-white py-2 px-4 rounded flex items-center"
  disabled={subscriptionLoading}
>
  Manage Plans
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    className="h-4 w-4 ml-2" 
    fill="none" 
    viewBox="0 0 24 24" 
    stroke="currentColor" 
    strokeWidth={2}
  >
    <path 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" 
    />
  </svg>
</button> */}
        </div>

        {/* Subscription History Section */}
        {/* <div className="mb-8 p-6 bg-white rounded-lg shadow-md">
          <h2 className="text-xl font-semibold mb-4">Subscription History</h2>
          <SubscriptionHistory />
        </div> */}

        {/* Delete Account Section */}
        {/* <div className="p-6 bg-white rounded-lg shadow-md">
          <h2 className="text-xl font-semibold mb-4">Delete Account</h2>
          <p className='text-md'>Once you delete your account, it cannot be undone. This is permanent.</p>
          {deleteError && (
            <div className="mt-2 mb-2 p-3 bg-red-100 text-red-700 rounded">
              {deleteError}
            </div>
          )}
          <button
            onClick={handleDeleteAccount}
            className="bg-red-500 hover:bg-red-700 text-white py-2 px-4 rounded mt-4"
            disabled={isDeletingAccount}
          >
            {isDeletingAccount ? 'Processing...' : 'Delete Account'}
          </button>
        </div> */}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isVisible={isDeleteModalVisible}
        onClose={() => setIsDeleteModalVisible(false)}
        onConfirm={confirmDeleteAccount}
        title="Delete Account"
        message="Are you sure you want to delete your account? This action cannot be undone and you will lose access to all your data."
        confirmText="Delete"
        cancelText="Cancel"
      />
    </>
  );
};

export default MyAccount;